import type { ReactNode } from 'react';
import { Tag, cn } from '../ui';
import type { TagTone } from '../ui';
// Imported rather than re-picked, so a Head is the same gold on a filter as on their own badge.
import { TAG_TONE_HUE } from '../lib/palette';

/**
 * A person's role as a COLOURED PILL, for use right beside their name.
 *
 * Lists of people are mixed (a Sales Head sitting between two Sales Persons, an Admin who marked a
 * row on someone's behalf), and a grey caption under the name made the reader parse text to tell
 * them apart. One hue per role lets the mix be read at a glance instead.
 *
 * The tone is matched by PATTERN, not by an exact key, because the same role reaches the UI under
 * three spellings: the DB role code (`SALES_HEAD`), the role's display name (`Sales Head`) and the
 * seeded long form (`Super Administrator`). Order matters — WAREHOUSE is tested before ADMIN, and
 * HEAD before the generic SALES, so "Warehouse Manager" and "Sales Head" don't fall through to the
 * broader rule underneath them.
 */
const RULES: Array<{ re: RegExp; tone: TagTone }> = [
  { re: /SUPER/, tone: 'purple' },        // Super Admin — the highest authority, its own hue
  { re: /WAREHOUSE|^WHM$/, tone: 'cyan' },// Warehouse Manager (before ADMIN: it holds no "admin")
  { re: /ADMIN/, tone: 'red' },           // Administrator
  { re: /ACCOUNT|FINANCE/, tone: 'blue' },// Accounts
  { re: /HEAD|^RSM$|REGIONAL/, tone: 'gold' }, // Sales Head — senior field role
  { re: /SALES|^SP$/, tone: 'green' },    // Sales Person — the everyday field rep
  { re: /RETAILER|DEALER|DISTRIBUTOR/, tone: 'neutral' },
  { re: /FARMER|CUSTOMER/, tone: 'neutral' },
];

/** Long seeded names that would crowd a table cell; shown short, meaning unchanged. */
const SHORT: Record<string, string> = {
  SUPER_ADMINISTRATOR: 'Super Admin',
  SUPER_ADMIN: 'Super Admin',
  ADMINISTRATOR: 'Admin',
  WAREHOUSE_MANAGER: 'Warehouse Mgr',
  FARMER_CUSTOMER: 'Farmer',
};

/**
 * THE PILL'S OWN TEXT — the role as a short code.
 *
 * The badge repeats on every row of a register: a column of "Sales Person" pills is the same three
 * syllables forty times over, taking the width of a name to say what the colour already says. The
 * pill carries the code and the hue; the full role is one hover away in `title`, and is what a
 * screen reader is given, so nothing is actually lost.
 *
 * Matched by PATTERN in the same order as the tones above — WAREHOUSE before ADMIN, HEAD before the
 * generic SALES — so the three spellings of each role all land on one code. Customer roles are
 * deliberately absent: "Retailer" beside a dealer's name is read by people who do not work here,
 * and it is already one short word.
 */
const ABBR: Array<{ re: RegExp; short: string }> = [
  { re: /SUPER/, short: 'SA' },
  { re: /WAREHOUSE|^WHM$/, short: 'WHM' },
  { re: /ADMIN/, short: 'Admin' },
  { re: /ACCOUNT|FINANCE/, short: 'Acc' },
  { re: /HEAD|^RSM$|REGIONAL/, short: 'SH' },
  { re: /SALES|^SP$/, short: 'SP' },
];

/** `Sales Head` / `SALES_HEAD` / `Super Administrator` → one comparable key. */
const keyOf = (role: string) => role.trim().toUpperCase().replace(/[^A-Z0-9]+/g, '_').replace(/^_|_$/g, '');

export function roleTone(role?: string | null): TagTone {
  if (!role) return 'neutral';
  const k = keyOf(role);
  return RULES.find((r) => r.re.test(k))?.tone ?? 'neutral';
}

/** Display text: a short form for the known long names, else the given label — a CODE title-cased. */
export function roleLabel(role: string): string {
  const k = keyOf(role);
  if (SHORT[k]) return SHORT[k];
  // Already a display name ("Sales Head") — leave it alone; a CODE ("SALES_HEAD") gets title-cased.
  if (/[a-z]/.test(role)) return role.trim();
  return k.split('_').map((w) => w.charAt(0) + w.slice(1).toLowerCase()).join(' ');
}

/** The pill's text — a code for the staff roles, the full label for everyone else. */
export function roleShort(role: string): string {
  const k = keyOf(role);
  return ABBR.find((a) => a.re.test(k))?.short ?? roleLabel(role);
}

export function RoleBadge({ role, className }: { role?: string | null; className?: string }) {
  if (!role) return null;
  const full = roleLabel(role);
  const short = roleShort(role);
  return (
    <Tag
      tone={roleTone(role)}
      className={cn('shrink-0', className)}
      title={short === full ? undefined : full}
      aria-label={short === full ? undefined : full}
    >
      {short}
    </Tag>
  );
}

/** The hue a person's role wears — for tinting a control that has one of them selected. */
export function roleHue(role?: string | null): string | null {
  return role ? (TAG_TONE_HUE[roleTone(role)] ?? null) : null;
}

/**
 * Every role a user holds — the primary one first, then any additional links, de-duplicated.
 *
 * De-duplication is not tidiness: a user seeded with SALES_PERSON both as their primary role and as
 * an explicit `user_roles` link would otherwise wear that pill twice wherever this is mapped.
 */
export function rolesOfUser(u: any): string[] {
  return [...new Set(
    [u?.role?.code ?? u?.roleCode, ...((u?.additionalRoles ?? []).map((r: any) => r?.code ?? r))]
      .filter(Boolean).map(String),
  )];
}

export interface StaffOption { value: string; name: string; title: string; label: ReactNode }

/**
 * A PICKER OF PEOPLE — grouped by role, with the role on every option.
 *
 * Group headings alone do not do it: they scroll off the top of a long list, so most of what you are
 * looking at sits under no visible heading, and a rep who is ALSO a Head is filed under ONE heading
 * while holding TWO roles. The pill travels with the name instead, so an option states what the
 * person is wherever it is read. The headings stay — they are still the fastest way to a block.
 *
 * Roles are primary-first and DE-DUPLICATED: a user seeded with SALES_PERSON both as their primary
 * role and as an explicit `user_roles` link would otherwise wear that pill twice.
 *
 * `name` is the plain text, and every caller must pass `optionFilterProp="name"` AND
 * `optionLabelProp="name"`: type-to-search matches the name, and the CLOSED control shows the name
 * alone, because a pill inside a 9rem filter cell pushes out the name it belongs to. `title` is the
 * trigger's hover text and what an applied-filter chip prints — a node in a template literal renders
 * "[object Object]".
 *
 * One definition, shared: two copies is how the same person ends up badged on one screen and bare on
 * the next.
 */
export function staffPickerOptions(list: any[]): Array<{ label: string; options: StaffOption[] }> {
  const isHead = (u: any) => rolesOfUser(u).includes('SALES_HEAD');
  const opt = (u: any): StaffOption => ({
    value: String(u.id),
    name: String(u.fullName ?? ''),
    title: String(u.fullName ?? ''),
    label: (
      <span className="flex w-full items-center gap-2">
        <span className="min-w-0 flex-1 truncate">{u.fullName}</span>
        {rolesOfUser(u).map((c) => <RoleBadge key={c} role={c} />)}
      </span>
    ),
  });
  const byName = (a: StaffOption, b: StaffOption) => a.name.localeCompare(b.name);
  return [
    { label: 'Sales Heads', options: list.filter(isHead).map(opt).sort(byName) },
    { label: 'Sales Persons', options: list.filter((u) => !isHead(u)).map(opt).sort(byName) },
  ].filter((g) => g.options.length > 0);
}
